import Link from 'next/link';
import styled from 'styled-components';
import { palette } from 'styled-tools';

import { Text } from '@calm-web/design-system';

import chevronIcon from 'icons/chevron-left-aligned.svg';

// Matches Android's AssociationView (view_association.xml) exactly: no background fill —
// the row sits directly on the page, with only vertical padding (fixed_half_margin, 4dp) —
// and the chevron pinned to the far right via justify-content: space-between, with the
// icon+text grouped on the left (LeftGroup) so that space has somewhere to go.
// Capped at 480px to match PackRowList's own row-list precedent (RowContainer, PlaylistInfo
// max-width: 480px) — mobile's "full width" is inherently a phone-proportioned column, so
// the row's stretch-to-fill/chevron-at-the-end behavior needs an equivalent cap on desktop's
// much wider content column.
const rowLayoutCSS = `
	align-items: center;
	display: flex;
	justify-content: space-between;
	max-width: 480px;
	padding: 4px 0;
	width: 100%;
`;

// A real navigation link (to the linked profile), not a static label — matches the
// chevron-suffixed row convention PackClassLink already uses for "this text goes somewhere".
// Only used when `href` is passed; see RowStatic for the non-linked case.
export const Row = styled(Link)`
	${rowLayoutCSS}
	text-decoration: none;

	&:hover {
		opacity: 0.8;
	}
`;

export const RowStatic = styled.div`
	${rowLayoutCSS}
`;

// Android's half_margin (4dp) between icon and text.
export const LeftGroup = styled.div`
	align-items: center;
	display: flex;
	gap: 4px;
	min-width: 0;
`;

// Android's program_header_narrator_icon_size (44dp).
export const ImageWrapper = styled.div`
	border-radius: 50%;
	flex-shrink: 0;
	height: 44px;
	overflow: hidden;
	position: relative;
	width: 44px;
`;

// Text's default margin-block-start/end (0.5em each) wasn't killed here, so Title and
// Subtitle — at different font sizes — each carried a different em-based margin instead of
// a single flat, deliberate gap between them. noMargin below removes that; this gap is the
// real, intentional one — iOS uses a flat 2pt (CatalogBylineRow's VStack(spacing: 2)),
// Android a flat 4dp (view_association.xml's fixed_half_margin); 3px splits the two.
export const TextColumn = styled.div`
	display: flex;
	flex-direction: column;
	gap: 3px;
	min-width: 0;
`;

// Regular weight, deliberately — Android's Text.Subhead declares fontWeight:600 but that's
// dead styling metadata (no custom attr backs it, and its font family resolves to a static
// 400-weight-only .ttf, so the name genuinely renders regular there); iOS's subhead really is
// Figtree-SemiBold, but reads visually close to regular at 13pt. Both platforms look
// un-bolded to the eye — verified directly against real screenshots, not just source intent.
export const Title = styled(Text).attrs({
	el: 'p',
	styleAs: 'h6',
	noMargin: true,
})`
	color: ${palette('white')};
	font-size: 13px;
`;

export const Subtitle = styled(Text).attrs({
	el: 'p',
	styleAs: 'h6',
	noMargin: true,
})`
	color: ${palette('whiteAlpha70')};
	font-size: 12px;
`;

// Android's own chevron asset (icon_vector_chevron_right) is a thin 6dp x 12dp arrow, but
// chevronIcon here has a square 96x96 viewBox — forcing a 6x12 box would squash it
// non-uniformly, so this keeps it square at Android's 12dp height instead.
// The 4px end margin drops this onto the same trailing line as the page's fave hearts. Left
// flush to the column it centred 4px further out than they do, which on web — where the
// narrator row sits between the title's heart and the list's — read as a misalignment.
export const Chevron = styled(chevronIcon)`
	flex-shrink: 0;
	height: 12px;
	margin-inline-end: 4px;
	margin-left: 6px;
	transform: rotate(180deg);
	width: 12px;

	& path {
		fill: ${palette('white')};
	}
`;
